import { useCallback, useEffect, useState } from "react";

declare global {
  interface Window {
    kakao: any;
  }
}

let loadPromise: Promise<void> | null = null;
let kakaoMapsReady = false;

const KAKAO_MAP_SCRIPT_ID = "kakao-maps-sdk";
const KAKAO_MAP_LOAD_TIMEOUT_MS = 12_000;

const isKakaoMapsLoadable = () =>
  typeof window.kakao?.maps?.load === "function";

const isKakaoMapsUsable = () =>
  typeof window.kakao?.maps?.Map === "function" &&
  typeof window.kakao?.maps?.services?.Geocoder === "function";

const removeFailedScript = () => {
  const script = document.getElementById(
    KAKAO_MAP_SCRIPT_ID
  ) as HTMLScriptElement | null;
  if (script?.dataset.loadState === "failed") {
    script.remove();
  }
};

const loadKakaoScript = (appKey: string): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }

  if (kakaoMapsReady && isKakaoMapsUsable()) {
    return Promise.resolve();
  }

  if (kakaoMapsReady) {
    kakaoMapsReady = false;
  }

  if (loadPromise) {
    return loadPromise;
  }

  removeFailedScript();

  const attempt = new Promise<void>((resolve, reject) => {
    let settled = false;
    let timeoutId: number;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      callback();
    };

    const finishSuccess = () => {
      finish(() => {
        kakaoMapsReady = true;
        resolve();
      });
    };

    const finishError = (error: Error) => {
      const script = document.getElementById(
        KAKAO_MAP_SCRIPT_ID
      ) as HTMLScriptElement | null;
      if (script && !isKakaoMapsUsable()) {
        script.dataset.loadState = "failed";
      }
      finish(() => reject(error));
    };

    timeoutId = window.setTimeout(() => {
      finishError(new Error("Kakao Maps SDK load timed out"));
    }, KAKAO_MAP_LOAD_TIMEOUT_MS);

    const onReady = () => {
      if (!isKakaoMapsLoadable()) {
        finishError(new Error("Failed to load Kakao Maps SDK"));
        return;
      }

      try {
        window.kakao.maps.load(() => {
          if (!isKakaoMapsUsable()) {
            finishError(new Error("Failed to initialize Kakao Maps SDK"));
            return;
          }
          finishSuccess();
        });
      } catch (error) {
        finishError(
          error instanceof Error
            ? error
            : new Error("Failed to initialize Kakao Maps SDK")
        );
      }
    };

    if (isKakaoMapsLoadable()) {
      onReady();
      return;
    }

    const existing = document.getElementById(
      KAKAO_MAP_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existing) {
      if (existing.dataset.loadState === "loaded") {
        existing.remove();
        appendScript();
        return;
      }
      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener(
        "error",
        () =>
          finishError(new Error("Failed to load Kakao Maps SDK")),
        { once: true }
      );
      return;
    }

    appendScript();

    function appendScript() {
      const script = document.createElement("script");
      script.id = KAKAO_MAP_SCRIPT_ID;
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&autoload=false&libraries=services,clusterer`;
      script.async = true;
      script.dataset.loadState = "loading";
      script.onload = () => {
        script.dataset.loadState = "loaded";
        onReady();
      };
      script.onerror = () => {
        script.dataset.loadState = "failed";
        finishError(new Error("Failed to load Kakao Maps SDK"));
      };
      document.head.appendChild(script);
    }
  });

  loadPromise = attempt;
  void attempt.catch(() => {
    if (loadPromise === attempt) {
      loadPromise = null;
    }
  });
  return attempt;
};

export const useKakaoMap = () => {
  const [ready, setReady] = useState<boolean>(kakaoMapsReady);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    if (kakaoMapsReady && isKakaoMapsUsable()) {
      setReady(true);
      setError(null);
      return;
    }
    loadPromise = null;
    removeFailedScript();
    setReady(false);
    setError(null);
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    const key = import.meta.env.VITE_KAKAO_MAP_KEY as string | undefined;
    if (!key) {
      setError(new Error("VITE_KAKAO_MAP_KEY 환경변수가 설정되지 않았습니다."));
      return;
    }
    let canceled = false;
    loadKakaoScript(key)
      .then(() => {
        if (!canceled) {
          setReady(true);
        }
      })
      .catch((e: Error) => {
        if (!canceled) {
          setError(e);
        }
      });
    return () => {
      canceled = true;
    };
  }, [attempt]);

  return { ready, error, retry };
};
